/**
 * Rules of the player-made clans: the clan name, the description, the emblems a clan may choose and the reserved words.
 * ONE module, browser-safe (no Node, DOM or protocol imports), like ./auth-rules.ts: the server applies it
 * authoritatively (src/server/core/services/clans.ts) and a client of any platform uses the same functions for instant
 * feedback (system.hello `limits.clans` publishes the numbers). The reasons are the stable `details.reason` values of the
 * protocol's VALIDATION_FAILED errors (src/shared/protocol/reasons.ts).
 *
 * Name
 *  - The input is NFC-normalized, the typographic apostrophes (U+2019, U+02BC) become `'`, every run of white space
 *    becomes one space, and both ends are trimmed; the stored name keeps the player's case.
 *  - 3..20 characters (code points) after that.
 *  - Allowed: Latin letters, Cyrillic letters (U+0400..U+045F, Ґґ), digits, single inner spaces, `-`, `.` and `'`.
 *    At least one letter, and letters of ONE script (as in nicknames: a Cyrillic "а" inside a Latin name would
 *    impersonate another clan).
 *  - Uniqueness: `clanNameKey` - lower case, the Cyrillic letters that look like Latin ones replaced by their Latin
 *    twins (the nickname skeleton), spaces, `-`, `.` and `'` dropped. "Wolf Pack", "wolf-pack" and "WOLFPACK" (and the
 *    same letters typed in Cyrillic lookalikes) are one name.
 *  - Reserved words (CLAN_RESERVED_WORDS and the operator's deny list, CLAN_NAME_DENYLIST_FILE): a name or description
 *    whose `clanTextKey` contains the key of one of them is refused (clan_name_denied / description_denied).
 * Description
 *  - Optional (empty = none), normalized like the name, at most 120 characters; no control or format characters and
 *    no lone UTF-16 surrogates (half of an emoji: not text, and strict JSON decoders of other platforms refuse the
 *    whole message that carries one). A name cannot hold one either: its characters are listed above.
 */

export const CLAN_NAME_MIN = 3;
export const CLAN_NAME_MAX = 20;
export const CLAN_DESCRIPTION_MAX = 120;
/** Bounds of the name search of clan.list (`query`). */
export const CLAN_QUERY_MIN = 2;
export const CLAN_QUERY_MAX = 20;
/**
 * Bounds of the request payload schema: generous, so that the service answers a too long value with its specific
 * reason; anything longer is a malformed payload (BAD_REQUEST 'invalid_payload').
 */
export const CLAN_NAME_INPUT_MAX = 64;
export const CLAN_DESCRIPTION_INPUT_MAX = 512;

/**
 * The emblems a clan may choose: frames of the game's `clan_icons` sprite. 2, 3, 7, 9, 10, 11, 13 are drawings of the
 * original that belong to nobody else (the peach of its developer, logos of other games and the hammer and sickle are
 * not offered); 15..22 are the game's own flags (sheep, three worms, banana, radiation, saint, ruby, zombie, devil)
 * drawn into the free frames by the client's asset patches.
 */
export const CLAN_EMBLEMS: readonly number[] = [2, 3, 7, 9, 10, 11, 13, 15, 16, 17, 18, 19, 20, 21, 22];

/**
 * Words no clan name or description may contain (compared by `clanTextKey`, so case, lookalike letters, spaces and
 * punctuation do not help): the names of the staff and the server, and the name that stands for deleted players
 * (DELETED_PLAYER_NAME '[deleted]' of the protocol model).
 */
export const CLAN_RESERVED_WORDS: readonly string[] = ['admin', 'адмін', 'админ', 'operator', 'оператор', 'moderator', 'модератор', 'fwp', 'system', 'система', '[deleted]'];

export type ClanNameProblem = 'clan_name_required' | 'clan_name_too_short' | 'clan_name_too_long' | 'clan_name_invalid';
export type ClanDescriptionProblem = 'description_too_long' | 'description_invalid';

export type ClanNameCheck = { ok: true; name: string; key: string } | { ok: false; field: 'name'; reason: ClanNameProblem };
export type ClanDescriptionCheck = { ok: true; description: string } | { ok: false; field: 'description'; reason: ClanDescriptionProblem };

const LETTER_CLASS = 'A-Za-z\\u0400-\\u045F\\u0490\\u0491';
const WORD_CLASS = `${LETTER_CLASS}0-9.'\\-`;
/** Allowed characters with single spaces between them (the input is already normalized). */
const NAME_PATTERN = new RegExp(`^[${WORD_CLASS}]+(?: [${WORD_CLASS}]+)*$`);
const LETTER = new RegExp(`[${LETTER_CLASS}]`);
const LATIN_LETTER = /[A-Za-z]/;
const CYRILLIC_LETTER = /[Ѐ-џҐґ]/;
/**
 * Control and format characters (zero-width, bidirectional overrides...) and lone surrogates (\p{Cs}: with the u flag
 * a surrogate pair is one code point, so only an unpaired half matches; a regular expression, not
 * String.prototype.isWellFormed, because this module runs in older browsers too).
 */
const CONTROL_FORMAT_OR_SURROGATE = /[\p{Cc}\p{Cf}\p{Cs}]/u;

/** Cyrillic letters that look like Latin ones -> the Latin twin (the nickname skeleton of ./auth-rules.ts). */
const CYRILLIC_TO_LATIN: ReadonlyMap<string, string> = new Map([
  ['а', 'a'],
  ['в', 'b'],
  ['с', 'c'],
  ['е', 'e'],
  ['н', 'h'],
  ['і', 'i'],
  ['ј', 'j'],
  ['к', 'k'],
  ['м', 'm'],
  ['о', 'o'],
  ['р', 'p'],
  ['ѕ', 's'],
  ['т', 't'],
  ['х', 'x'],
  ['у', 'y'],
]);

function codePoints(s: string): number {
  let n = 0;
  for (const _ of s) n++;
  return n;
}

/** NFC, typographic apostrophes as `'`, white space runs as one space, trimmed (case kept). */
export function normalizeClanText(raw: string): string {
  return String(raw).normalize('NFC').replace(/[’ʼ]/g, "'").replace(/\s+/g, ' ').trim();
}

/** Lower case with the Cyrillic lookalikes spelled in Latin letters. */
function skeleton(text: string): string {
  let out = '';
  for (const c of text.toLowerCase()) out += CYRILLIC_TO_LATIN.get(c) ?? c;
  return out;
}

/** The uniqueness key of a clan name (see the module comment): also the id of its C.clanNames document. */
export function clanNameKey(raw: string): string {
  return skeleton(normalizeClanText(raw)).replace(/[ .'-]/g, '');
}

/** The comparison key of any clan text (reserved words, search): the skeleton without anything but letters and digits. */
export function clanTextKey(raw: string): string {
  return skeleton(normalizeClanText(raw)).replace(/[^\p{L}\p{N}]/gu, '');
}

/** Checks a clan name (see the module comment); on success the stored form and the key. */
export function checkClanName(raw: unknown): ClanNameCheck {
  if (typeof raw !== 'string') return { ok: false, field: 'name', reason: 'clan_name_required' };
  const name = normalizeClanText(raw);
  if (name === '') return { ok: false, field: 'name', reason: 'clan_name_required' };
  if (!NAME_PATTERN.test(name)) return { ok: false, field: 'name', reason: 'clan_name_invalid' };
  const length = codePoints(name);
  if (length < CLAN_NAME_MIN) return { ok: false, field: 'name', reason: 'clan_name_too_short' };
  if (length > CLAN_NAME_MAX) return { ok: false, field: 'name', reason: 'clan_name_too_long' };
  if (!LETTER.test(name)) return { ok: false, field: 'name', reason: 'clan_name_invalid' };
  if (LATIN_LETTER.test(name) && CYRILLIC_LETTER.test(name)) return { ok: false, field: 'name', reason: 'clan_name_invalid' };
  return { ok: true, name, key: clanNameKey(name) };
}

/** Checks a clan description (see the module comment); '' = no description. */
export function checkClanDescription(raw: unknown): ClanDescriptionCheck {
  if (raw === undefined || raw === null) return { ok: true, description: '' };
  if (typeof raw !== 'string') return { ok: false, field: 'description', reason: 'description_invalid' };
  const description = normalizeClanText(raw);
  if (CONTROL_FORMAT_OR_SURROGATE.test(description)) return { ok: false, field: 'description', reason: 'description_invalid' };
  if (codePoints(description) > CLAN_DESCRIPTION_MAX) return { ok: false, field: 'description', reason: 'description_too_long' };
  return { ok: true, description };
}

/**
 * Whether `text` contains a reserved word (CLAN_RESERVED_WORDS and `extra`, the operator's deny list), compared by
 * clanTextKey. Returns the matching word, or null.
 */
export function reservedWordIn(text: string, extra: readonly string[] = []): string | null {
  const key = clanTextKey(text);
  if (key === '') return null;
  for (const word of [...CLAN_RESERVED_WORDS, ...extra]) {
    const w = clanTextKey(word);
    if (w !== '' && key.includes(w)) return word;
  }
  return null;
}

/** Whether `emblem` is one of `allowed` (default CLAN_EMBLEMS). */
export function isClanEmblem(emblem: unknown, allowed: readonly number[] = CLAN_EMBLEMS): emblem is number {
  return typeof emblem === 'number' && Number.isInteger(emblem) && allowed.includes(emblem);
}
